export class FormValidator {
    constructor(rules) {
        Object.defineProperty(this, "rules", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.rules = rules;
    }
    validate(data) {
        const errors = {};
        Object.keys(this.rules).forEach(fieldName => {
            const value = data[fieldName];
            const rule = this.rules[fieldName];
            const error = this.validateField(value, rule, fieldName);
            if (error) {
                errors[fieldName] = error;
            }
        });
        return errors;
    }
    validateField(value, rule, fieldName) {
        // Required validation
        if (rule.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
            return `${this.getFieldDisplayName(fieldName)} é obrigatório`;
        }
        // Skip other validations if value is empty and not required
        if (!value || (typeof value === 'string' && value.trim() === '')) {
            return null;
        }
        // Min length validation
        if (rule.minLength && typeof value === 'string' && value.length < rule.minLength) {
            return `${this.getFieldDisplayName(fieldName)} deve ter pelo menos ${rule.minLength} caracteres`;
        }
        // Max length validation
        if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
            return `${this.getFieldDisplayName(fieldName)} deve ter no máximo ${rule.maxLength} caracteres`;
        }
        // Pattern validation
        if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
            return `${this.getFieldDisplayName(fieldName)} tem formato inválido`;
        }
        // Email validation
        if (rule.email && typeof value === 'string') {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(value)) {
                return `${this.getFieldDisplayName(fieldName)} deve ser um email válido`;
            }
        }
        // Custom validation
        if (rule.custom) {
            const customError = rule.custom(value);
            if (customError) {
                return customError;
            }
        }
        return null;
    }
    getFieldDisplayName(fieldName) {
        const displayNames = {
            name: 'Nome',
            email: 'Email',
            password: 'Senha',
            confirmPassword: 'Confirmação de senha',
            currentPassword: 'Senha atual',
            newPassword: 'Nova senha',
            role: 'Perfil',
            accountId: 'Conta',
            description: 'Descrição',
            permissions: 'Permissões',
        };
        return displayNames[fieldName] || fieldName;
    }
    static validatePassword(password) {
        if (password.length < 6) {
            return 'A senha deve ter pelo menos 6 caracteres';
        }
        if (!/(?=.*[a-z])/.test(password)) {
            return 'A senha deve conter pelo menos uma letra minúscula';
        }
        if (!/(?=.*[A-Z])/.test(password)) {
            return 'A senha deve conter pelo menos uma letra maiúscula';
        }
        if (!/(?=.*\d)/.test(password)) {
            return 'A senha deve conter pelo menos um número';
        }
        return null;
    }
    static validatePasswordConfirmation(password, confirmPassword) {
        if (password !== confirmPassword) {
            return 'As senhas não coincidem';
        }
        return null;
    }
    static validateEmail(email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(email)) {
            return 'Email deve ter um formato válido';
        }
        return null;
    }
    static validateRequired(value, fieldName) {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
            return `${fieldName} é obrigatório`;
        }
        return null;
    }
}
// Predefined validation rules
export const validationRules = {
    login: {
        email: { required: true, email: true },
        password: { required: true, minLength: 1 },
    },
    register: {
        name: { required: true, minLength: 2, maxLength: 100 },
        email: { required: true, email: true },
        password: {
            required: true,
            minLength: 6,
            custom: (value) => FormValidator.validatePassword(value)
        },
        confirmPassword: { required: true },
    },
    changePassword: {
        currentPassword: { required: true, minLength: 1 },
        newPassword: {
            required: true,
            minLength: 6,
            custom: (value) => FormValidator.validatePassword(value)
        },
        confirmPassword: { required: true },
    },
    createUser: {
        name: { required: true, minLength: 2, maxLength: 100 },
        email: { required: true, email: true },
        password: {
            required: true,
            minLength: 6,
            custom: (value) => FormValidator.validatePassword(value)
        },
        role: { required: true },
        accountId: { required: true },
    },
    updateUser: {
        name: { minLength: 2, maxLength: 100 },
        email: { email: true },
        role: { required: true },
    },
    createAccount: {
        name: { required: true, minLength: 2, maxLength: 100 },
        description: { maxLength: 500 },
        owner: {
            name: { required: true, minLength: 2, maxLength: 100 },
            email: { required: true, email: true },
            password: {
                required: true,
                minLength: 6,
                custom: (value) => FormValidator.validatePassword(value)
            },
        },
    },
    updateAccount: {
        name: { minLength: 2, maxLength: 100 },
        description: { maxLength: 500 },
    },
    createRole: {
        name: { required: true, minLength: 2, maxLength: 50 },
        description: { required: true, minLength: 10, maxLength: 200 },
        permissions: { required: true },
    },
    updateRole: {
        name: { minLength: 2, maxLength: 50 },
        description: { minLength: 10, maxLength: 200 },
    },
};
// Validation helpers
export const validateForm = (data, rules) => {
    const validator = new FormValidator(rules);
    return validator.validate(data);
};
export const hasErrors = (errors) => {
    return Object.keys(errors).length > 0;
};
export const getFirstError = (errors) => {
    const firstKey = Object.keys(errors)[0];
    return firstKey ? errors[firstKey] : null;
};
